// src/pages/caisse/OperationsCaisse.jsx
import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, Button, Pagination, 
    Modal, Alert, Spinner, Badge 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

export default function OperationsCaisse() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [operationToDelete, setOperationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [stats, setStats] = useState(null);
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    useEffect(() => {
        fetchOperations();
        fetchStats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const fetchOperations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const handleDeleteClick = (operation) => {
        setOperationToDelete(operation);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!operationToDelete) return;
        
        setDeleting(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${operationToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setOperationToDelete(null);
                fetchOperations();
                fetchStats();
                showSuccess("Opération supprimée avec succès");
            } else {
                alert(result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const toggleExpandRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Ne montrer que les opérations initiales dans la liste principale
    const mainOperations = useMemo(() => {
        return data.filter(op => op.type === 'initial' || !op.parent_id);
    }, [data]);

    // Enrichir les opérations avec leurs montants calculés
   // src/pages/caisse/OperationsCaisse.jsx
// Version corrigée - Utilise directement les données de l'API

// Supprimez ou commentez la fonction enrichedOperations actuelle
// et remplacez-la par :

// Utiliser directement les données de l'API qui sont déjà enrichies
const enrichedOperations = useMemo(() => {
    // L'API retourne déjà les champs calculés :
    // - montant_total
    // - avance_initiale
    // - total_verse
    // - reste_reel
    // - somme_paiements
    // - nb_paiements
    // - pourcentage_paye
    
    return mainOperations.map(op => ({
        ...op,
        montantTotal: op.montant_total || op.montant_initial || op.montant || 0,
        avanceInitiale: op.avance_initiale || op.avance || 0,
        totalVersé: op.total_verse || op.avance || 0,
        resteReel: op.reste_reel || op.reste || 0,
        sommePaiements: op.somme_paiements || 0,
        nbPaiements: op.nb_paiements || (op.paiements?.length || 0),
        pourcentagePaye: op.pourcentage_paye || 0
    }));
}, [mainOperations]);

// La fonction getPaiementsWithCalcul reste la même car elle calcule
// l'historique séquentiel des paiements pour l'affichage détaillé
    // Récupérer les paiements d'une opération avec calcul séquentiel
    const getPaiementsWithCalcul = (parentOperation) => {
        const paiements = data.filter(op => op.parent_id === parentOperation.id);
        const sortedPaiements = [...paiements].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        
        const montantTotal = parentOperation.montant_initial || parentOperation.montant || 0;
        const avanceInitiale = parentOperation.avance || 0;
        const paiementsAvecReste = [];
        let montantDuAvant = montantTotal - avanceInitiale;
        
        for (let i = 0; i < sortedPaiements.length; i++) {
            const paiement = sortedPaiements[i];
            const montantPaye = Number(paiement.avance) || 0;
            const nouveauReste = Math.max(0, montantDuAvant - montantPaye);
            
            paiementsAvecReste.push({
                ...paiement,
                montant_du_avant: montantDuAvant,
                montant_paye: montantPaye,
                reste_apres: nouveauReste
            });
            montantDuAvant = nouveauReste;
        }
        return paiementsAvecReste;
    };

    const filteredAndSortedItems = useMemo(() => {
        let filtered = enrichedOperations.filter(item => {
            const textMatch = 
                (item.beneficiaire_nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.beneficiaire_prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.numero_bordereau || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.motif || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.createur_nom || '').toLowerCase().includes(filterText.toLowerCase());
            
            let dateMatch = true;
            const itemDate = new Date(item.created_at).toISOString().split('T')[0];
            if (dateDebut && dateFin) {
                dateMatch = itemDate >= dateDebut && itemDate <= dateFin;
            } else if (dateDebut) {
                dateMatch = itemDate >= dateDebut;
            } else if (dateFin) {
                dateMatch = itemDate <= dateFin;
            }
            
            return textMatch && dateMatch;
        });

        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];
            
            if (sortField === 'montantTotal') {
                aValue = a.montantTotal || 0;
                bValue = b.montantTotal || 0;
            } else if (sortField === 'resteReel') {
                aValue = a.resteReel || 0;
                bValue = b.resteReel || 0;
            } else {
                aValue = a[sortField];
                bValue = b[sortField];
            }
            
            return sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
        });

        return filtered;
    }, [filterText, sortField, sortDirection, enrichedOperations, dateDebut, dateFin]);

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOperations(currentItems.map(op => op.id));
        } else {
            setSelectedOperations([]);
        }
    };

    const handleSelectOperation = (id) => {
        setSelectedOperations(prev => 
            prev.includes(id) ? prev.filter(opId => opId !== id) : [...prev, id]
        );
    };

    const isAllSelected = currentItems.length > 0 && selectedOperations.length === currentItems.length;
    const isSomeSelected = selectedOperations.length > 0 && selectedOperations.length < currentItems.length;

    const resetFilters = () => {
        setFilterText('');
        setDateDebut('');
        setDateFin('');
        setCurrentPage(1);
    };

    const handleExport = (format) => {
        setExportLoading(true);
        const dataToExport = selectedOperations.length > 0
            ? enrichedOperations.filter(op => selectedOperations.includes(op.id))
            : filteredAndSortedItems;

        if (!dataToExport.length) { 
            alert("Aucune donnée à exporter"); 
            setExportLoading(false); 
            return; 
        }

        if (format === "csv") {
            const headers = ["Bordereau","Bénéficiaire","Montant","Motif","Total versé","Reste","Date","Statut"];
            const csvContent = [
                "\uFEFF" + headers.join(";"),
                ...dataToExport.map(op => {
                    const statut = op.resteReel <= 0 ? 'Soldé' : (op.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
                    return [
                        op.numero_bordereau,
                        `${op.beneficiaire_prenom} ${op.beneficiaire_nom}`,
                        op.montantTotal,
                        op.motif,
                        op.totalVersé,
                        op.resteReel,
                        formatDate(op.created_at),
                        statut
                    ].map(v => `"${v}"`).join(";");
                })
            ].join("\n");
            downloadFile(csvContent, "text/csv;charset=utf-8;", `export-caisse-${Date.now()}.csv`);
        } else if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(dataToExport.map(op => {
                const statut = op.resteReel <= 0 ? 'Soldé' : (op.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
                return {
                    Bordereau: op.numero_bordereau,
                    Bénéficiaire: `${op.beneficiaire_prenom} ${op.beneficiaire_nom}`,
                    Montant: op.montantTotal,
                    Motif: op.motif,
                    "Avance initiale": op.avanceInitiale,
                    "Total versé": op.totalVersé,
                    Reste: op.resteReel,
                    Date: formatDate(op.created_at),
                    Statut: statut,
                    "Nb paiements": op.nbPaiements
                };
            }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Operations");
            XLSX.writeFile(wb, `export-caisse-${Date.now()}.xlsx`);
        } else if (format === "pdf") {
            generatePDF(dataToExport);
        }
        setExportLoading(false);
    };

    const generatePDF = (data) => {
        const doc = new jsPDF();
        doc.setFillColor(33, 172, 205);
        doc.rect(0, 0, 210, 25, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("HISTORIQUE DES DÉCAISSEMENTS", 105, 15, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
        doc.text(`Total: ${data.length} opération(s)`, 180, 22, { align: 'right' });
        
        const columns = ["BORDEREAU", "BÉNÉFICIAIRE", "MONTANT", "VERSÉ", "RESTE", "DATE", "STATUT"];
        const rows = data.map(item => {
            const statut = item.resteReel <= 0 ? 'Soldé' : (item.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
            return [
                item.numero_bordereau,
                `${item.beneficiaire_prenom} ${item.beneficiaire_nom}`,
                new Intl.NumberFormat('fr-FR').format(item.montantTotal) + ' FCFA',
                new Intl.NumberFormat('fr-FR').format(item.totalVersé) + ' FCFA',
                new Intl.NumberFormat('fr-FR').format(item.resteReel) + ' FCFA',
                formatDate(item.created_at),
                statut
            ];
        });
        
        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 35,
            theme: 'striped',
            headStyles: { fillColor: [33, 172, 205], textColor: 255, fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 35 },
                2: { cellWidth: 20, halign: 'right' },
                3: { cellWidth: 20, halign: 'right' },
                4: { cellWidth: 20, halign: 'right' },
                5: { cellWidth: 20 },
                6: { cellWidth: 20 }
            },
            margin: { left: 15, right: 15 }
        });
        doc.save(`caisse-${Date.now()}.pdf`);
    };

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    const SortableHeader = ({ field, children }) => (
        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(field)}>
            {children}
            {sortField === field ? (
                <i className={`ri-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-2`} style={{ fontSize: '12px' }}></i>
            ) : (
                <i className="ri-arrow-down-up ms-2" style={{ fontSize: '11px', opacity: 0.5 }}></i>
            )}
        </th>
    );

    if (loading && !mainOperations.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des opérations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !mainOperations.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchOperations}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                
                {showSuccessAlert && (
                    <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                        <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                        {successMessage}
                    </Alert>
                )}

                {/* En-tête */}
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                <i className="ri-bank-card-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                            </div>
                            <div>
                                <h4 className="main-title mb-0 fw-bold">Historique des décaissements</h4>
                                <p className="text-muted small mb-0">Suivi des opérations de caisse</p>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0">
                        <Button variant="outline-primary" size="sm" onClick={fetchOperations} disabled={loading} className="rounded-pill px-3 d-flex align-items-center gap-1" style={{ color: '#21ACCD', borderColor: '#21ACCD' }}>
                            <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
                            Actualiser
                        </Button>
                        <Button style={{ background: '#21ACCD', border: '0', color: 'white' }} as={Link} to="/caisse/decaissement" size="sm" className="rounded-pill px-3 d-flex align-items-center gap-1">
                            <i className="ri-add-line"></i>
                            Nouveau décaissement
                        </Button>
                    </div>
                </div>

                {/* KPI Cards */}
              {/* KPI Cards - Version corrigée utilisant stats de l'API */}
{stats && (
    <Row className="g-3 mb-4">
        <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-bank-card-line fs-24"></i></div>
                <div className="kpi-content">
                    <span className="kpi-label">Total décaissements</span>
                    <h3 className="kpi-value">{formatCurrency(stats.totaux?.total_decaissements || 0)}</h3>
                    <span className="kpi-unit">{stats.totaux?.total_operations || 0} bénéficiare(s)</span>
                </div>
            </div>
        </Col>
        <Col sm="6" xl="3">
            <div className="kpi-card kpi-danger">
                <div className="kpi-icon"><i className="ri-alert-line fs-24"></i></div>
                <div className="kpi-content">
                    <span className="kpi-label">Reste à payer</span>
                    <h3 className="kpi-value text-danger">{formatCurrency(stats.totaux?.reste_total || 0)}</h3>
                    <span className="kpi-unit">Montant impayé</span>
                </div>
            </div>
        </Col>
        <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-calendar-line fs-24"></i></div>
                <div className="kpi-content">
                    <span className="kpi-label">Aujourd'hui</span>
                    <h3 className="kpi-value">{formatCurrency(stats.aujourdhui?.montant_jour || 0)}</h3>
                    <span className="kpi-unit">{stats.aujourdhui?.nb_operations_jour || 0} opération(s)</span>
                </div>
            </div>
        </Col>
        <Col sm="6" xl="3">
            <div className="kpi-card kpi-info">
                <div className="kpi-icon"><i className="ri-bar-chart-2-line fs-24"></i></div>
                <div className="kpi-content">
                    <span className="kpi-label">Mois en cours</span>
                    <h3 className="kpi-value">{formatCurrency(stats.mois_courant?.montant_mois || 0)}</h3>
                    <span className="kpi-unit">{stats.mois_courant?.nb_operations_mois || 0} opération(s)</span>
                </div>
            </div>
        </Col>
    </Row>
)}

                {/* Filtres */}
                <Card className="border-0 shadow-sm rounded-3 mb-4">
                    <Card.Body className="py-3 px-4">
                        <Row className="align-items-center g-3">
                            <Col md="5">
                                <div className="position-relative">
                                    <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                    <Form.Control type="text" placeholder="Rechercher par bénéficiaire, bordereau, motif..." value={filterText} onChange={e => setFilterText(e.target.value)} className="ps-5 rounded-pill" />
                                </div>
                            </Col>
                            <Col md="3">
                                <Form.Control type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="rounded-pill" />
                            </Col>
                            <Col md="2">
                                <Form.Control type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="rounded-pill" />
                            </Col>
                            <Col md="2">
                                <div className="d-flex gap-2">
                                    <Form.Select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="rounded-pill px-3" style={{ width: '100px' }}>
                                        <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                                    </Form.Select>
                                    {(filterText || dateDebut || dateFin) && <Button variant="outline-secondary" size="sm" onClick={resetFilters} className="rounded-pill px-3"><i className="ri-close-line"></i></Button>}
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col className="text-end">
                                <span className="badge bg-light text-dark rounded-pill px-3 py-2"><i className="ri-stack-line me-1"></i>{filteredAndSortedItems.length} décaissement(s)</span>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Tableau des opérations */}
                <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                    <Card.Header className="border-0 py-3" style={{ background: '#21ACCD' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <i className="ri-history-line text-white me-2 fs-5"></i>
                                <span className="fw-semibold text-white">Historique des décaissements</span>
                                <Badge bg="light" text="dark" className="ms-2 rounded-pill">{filteredAndSortedItems.length} au total</Badge>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }} className="ps-4"><input type="checkbox" className="form-check-input rounded-checkbox" checked={isAllSelected && filteredAndSortedItems.length > 0} ref={input => { if (input) input.indeterminate = isSomeSelected; }} onChange={handleSelectAll} /></th>
                                        <SortableHeader field="numero_bordereau" className="col-bordereau">Bordereau</SortableHeader>
                                        <SortableHeader field="beneficiaire_nom">Bénéficiaire</SortableHeader>
                                        <SortableHeader field="montantTotal" className="text-end col-montant">Montant total</SortableHeader>
                                        <th className="text-end col-avance">Avance initiale</th>
                                        <th className="text-end col-totalVerse">Total versé</th>
                                        <SortableHeader field="resteReel" className="text-end col-reste">Reste</SortableHeader>
                                        <SortableHeader field="created_at" className="col-date">Date</SortableHeader>
                                        <th className="text-center pe-4 col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.length > 0 ? currentItems.map((operation) => {
                                        const paiementsAvecCalcul = getPaiementsWithCalcul(operation);
                                        const isExpanded = expandedRows[operation.id];
                                        return (
                                            <React.Fragment key={operation.id}>
                                                <tr className={isExpanded ? 'expanded-row' : ''}>
                                                    <td className="ps-4"><input type="checkbox" className="form-check-input rounded-checkbox" checked={selectedOperations.includes(operation.id)} onChange={() => handleSelectOperation(operation.id)} /></td>
                                                    <td className="col-bordereau"><span onClick={() => toggleExpandRow(operation.id)} className="reference-badge" style={{ cursor: 'pointer' }}>{operation.numero_bordereau}</span></td>
                                                    <td><div className="fw-semibold">{operation.beneficiaire_prenom} {operation.beneficiaire_nom}</div><small className="text-muted">{operation.motif?.substring(0, 40)}{operation.motif?.length > 40 ? '...' : ''}</small></td>
                                                    <td className="text-end col-montant"><span className="fw-bold">{formatCurrency(operation.montantTotal)}</span></td>
                                                    <td className="text-end col-avance"><span className="text-muted">{formatCurrency(operation.avanceInitiale)}</span></td>
                                                    <td className="text-end col-totalVerse"><span className="text-success fw-semibold">{formatCurrency(operation.totalVersé)}</span>{operation.nbPaiements > 0 && <small className="text-muted d-block">{operation.nbPaiements} opération(s)</small>}</td>
                                                    <td className="text-end col-reste"><span className={`fw-bold ${operation.resteReel <= 0 ? 'text-success' : 'text-danger'}`}>{formatCurrency(operation.resteReel)}</span></td>
                                                    <td className="col-date"><div className="small">{formatDate(operation.created_at)}</div><small className="text-muted">{new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small></td>
                                                    <td className="text-center pe-4 col-actions">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button className="action-icon action-icon-primary" onClick={() => navigate(`/caisse/operations/${operation.id}`)} title="Voir détails"><i className="ri-eye-line"></i></button>
                                                            <button className="action-icon action-icon-danger" onClick={() => handleDeleteClick(operation)} title="Supprimer"><i className="ri-delete-bin-line"></i></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {isExpanded && paiementsAvecCalcul.length > 0 && (
                                                    <tr className="sub-row">
                                                        <td colSpan="9" className="p-0">
                                                            <div className="p-3 ms-5 bg-light rounded-3">
                                                                <div className="d-flex align-items-center gap-2 mb-2"><i className="ri-history-line text-primary"></i><strong className="small">Historique des paiements</strong><Badge bg="secondary" className="rounded-pill">{paiementsAvecCalcul.length} opération(s)</Badge></div>
                                                                <Table size="sm" className="mb-0 bg-white rounded-3 overflow-hidden">
                                                                    <thead className="bg-light"><tr><th className="col-bordereau-sm">Bordereau</th><th className="col-date-sm">Date</th><th className="text-end col-montant-sm">Montant dû avant</th><th className="text-end col-montant-sm">Montant payé</th><th className="text-end col-reste-sm">Reste après</th><th className="text-center col-signature">Signature</th></tr></thead>
                                                                    <tbody>
                                                                        {paiementsAvecCalcul.map((p, idx) => (
                                                                            <tr key={idx}>
                                                                                <td className="col-bordereau-sm"><code className="small">{p.numero_bordereau}</code></td>
                                                                                <td className="col-date-sm">{formatDate(p.created_at)}<br/><small className="text-muted">{new Date(p.created_at).toLocaleTimeString('fr-FR')}</small></td>
                                                                                <td className="text-end col-montant-sm fw-bold">{formatCurrency(p.montant_du_avant)}</td>
                                                                                <td className="text-end col-montant-sm text-success fw-semibold">{formatCurrency(p.montant_paye)}</td>
                                                                                <td className="text-end col-reste-sm">{p.reste_apres <= 0 ? <span className="text-success fw-bold">Soldé</span> : <span className="text-danger fw-bold">{formatCurrency(p.reste_apres)}</span>}</td>
                                                                                <td className="text-center col-signature">{p.signature ? <i className="ri-check-line text-success fs-5" title="Signé"></i> : <i className="ri-close-line text-muted" title="Non signé"></i>}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    <tfoot className="bg-light"><tr><td colSpan="2" className="fw-semibold">Récapitulatif</td><td className="text-end fw-bold">{formatCurrency(operation.montantTotal)}</td><td className="text-end fw-bold text-success">{formatCurrency(operation.totalVersé)}</td><td className="text-end fw-bold text-danger">{formatCurrency(operation.resteReel)}</td><td></td></tr></tfoot>
                                                                </Table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    }) : (
                                        <tr><td colSpan="9" className="text-center py-5"><i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i><h6 className="mb-1">Aucune opération trouvée</h6><p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p></td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>

                {/* Pagination */}
                {totalPages > 1 && !loading && (
                    <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                        <div className="text-muted small">Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} décaissements</div>
                        <Pagination className="mb-0">
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} />
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                let pageNum = totalPages <= 5 ? i + 1 : (currentPage <= 3 ? i + 1 : (currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i));
                                return <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>{pageNum}</Pagination.Item>;
                            })}
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}

                <Footer />
            </div>

            {/* Modal de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100"><div className="text-center"><div className="confirm-icon mb-3"><i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i></div><h3 className="fw-bold text-white">Confirmer la suppression</h3></div></Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer ce décaissement ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3"><strong className="text-primary d-block">{operationToDelete?.numero_bordereau}</strong><small className="text-muted">{operationToDelete?.beneficiaire_prenom} {operationToDelete?.beneficiaire_nom}</small><div className="mt-1"><strong>{formatCurrency(operationToDelete?.montantTotal || 0)}</strong></div></div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">Annuler</Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting} className="rounded-pill px-5">{deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : 'Confirmer'}</Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
                .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: #21ACCD; } .kpi-danger { border-left-color: #ef4444; } .kpi-success { border-left-color: #10b981; } .kpi-info { border-left-color: #0dcaf0; }
                .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; } .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; } .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                .kpi-content { flex: 1; }
                .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; }
                .kpi-unit { font-size: 0.7rem; color: #6c757d; }
                .action-icon { width: 32px; height: 32px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; transition: all 0.2s; cursor: pointer; }
                .action-icon:hover { background: #21ACCD; border-color: #21ACCD; color: white; }
                .reference-badge { background: #f8f9fa; padding: 0.2rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; cursor: pointer; }
                .reference-badge:hover { background: #e9ecef; }
                .rounded-checkbox { width: 18px; height: 18px; border-radius: 6px !important; cursor: pointer; }
                .rounded-checkbox:checked { background-color: #21ACCD; border-color: #21ACCD; }
                .expanded-row { background-color: #f8fafc; }
                .col-bordereau { width: 120px; white-space: nowrap; }
                .col-montant { width: 110px; }
                .col-avance { width: 100px; }
                .col-totalVerse { width: 120px; }
                .col-reste { width: 110px; }
                .col-date { width: 130px; white-space: nowrap; }
                .col-actions { width: 80px; }
                .col-bordereau-sm { width: 100px; white-space: nowrap; }
                .col-date-sm { width: 110px; white-space: nowrap; }
                .col-montant-sm { width: 110px; }
                .col-reste-sm { width: 110px; }
                .col-signature { width: 60px; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
        </>
    );
}